import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import BackMethods from "../../back_component/BackMethods";
import {
    FaFileExcel,
    FaSearch,
    FaTable,
    FaCalendarAlt,
    FaFilter,
    FaChartBar,
    FaChevronDown,
    FaChevronUp,
    FaSlidersH,
    FaMapMarkerAlt,
    FaTimes,
    FaCheck,
    FaChartLine,
    FaArrowUp,
    FaHome,
    FaBox,
} from "react-icons/fa";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LabelList,
} from "recharts";
import "./PriceSurveyMatrixReportPage.css";

const DATE_COLUMN = "day";

const levelLabels = {
    governorate: "المحافظات",
    city: "المدن",
    district: "القطاعات",
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const daysAgoIso = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
};

const addOneDay = (dateStr) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const date = new Date(y, m - 1, d + 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const resolveLevel = ({ governorate, city }) => {
    if (city) return "district";
    if (governorate) return "city";
    return "governorate";
};

const isDateColumn = (col) => String(col).trim().toLowerCase() === DATE_COLUMN;

const formatPrice = (value, currency) => {
    if (value == null || value === "") return null;
    const num = Number(value);
    if (Number.isNaN(num)) return null;
    return currency === "USD"
        ? num.toLocaleString("ar-SY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : num.toLocaleString("ar-SY");
};

const formatDateLabel = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value ?? "");
    return date.toLocaleDateString("ar-SY", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

const customStyles = {
    control: (base) => ({
        ...base,
        borderRadius: "8px",
        minHeight: "42px",
        border: "1px solid #ced4da",
        fontSize: "14px",
    }),
};

const CHART_BAR_FILL = "#1565c0";

const formatChartPrice = (value, currency = "SYP") => {
    const num = Number(value);
    if (Number.isNaN(num)) return "";
    const suffix = currency === "USD" ? "$" : "ل.س";
    return `${num.toLocaleString("ar-SY")} ${suffix}`;
};

const ChartTooltip = ({ active, payload, currency }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0]?.payload;
    if (!item) return null;
    return (
        <div className="ps-matrix-chart-tooltip">
            <div className="ps-matrix-chart-tooltip__location">{item.location}</div>
            <div className="ps-matrix-chart-tooltip__price">{formatChartPrice(item.price, currency)}</div>
        </div>
    );
};

const createHorizontalBarLabel = (currency) => (props) => {
    const { x, y, width, height, value } = props;
    if (value == null || width < 36) return null;
    return (
        <text
            x={x + width / 2}
            y={y + height / 2}
            fill="#ffffff"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={12}
            fontWeight={700}
        >
            {formatChartPrice(value, currency)}
        </text>
    );
};

const createVerticalBarLabel = (currency) => (props) => {
    const { x, y, width, height, value } = props;
    if (value == null || height < 28) return null;
    return (
        <text
            x={x + width / 2}
            y={y + height / 2}
            fill="#ffffff"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={11}
            fontWeight={700}
        >
            {formatChartPrice(value, currency)}
        </text>
    );
};

const buildFiltersFromParams = (params, defaultGovernorate = "") => ({
    from: params.get("from") || daysAgoIso(30),
    to: params.get("to") || todayIso(),
    productId: params.get("productId") || "",
    governorate: params.get("governorate") || defaultGovernorate || "",
    city: params.get("city") || "",
    district: params.get("district") || "",
    establishmentUid: params.get("establishmentUid") || "",
});

const buildProductDaysReturnPath = (filterValues) => {
    const params = new URLSearchParams();
    params.set("autoLoad", "1");
    if (filterValues.productId) params.set("productId", filterValues.productId);
    if (filterValues.from) params.set("from", filterValues.from);
    if (filterValues.to) params.set("to", filterValues.to);
    if (filterValues.governorate) params.set("governorate", filterValues.governorate);
    if (filterValues.city) params.set("city", filterValues.city);
    if (filterValues.district) params.set("district", filterValues.district);
    if (filterValues.establishmentUid) params.set("establishmentUid", filterValues.establishmentUid);
    return `/admin/price-survey-product-days-report?${params.toString()}`;
};

export default function PriceSurveyProductDaysReportPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { backEnd_request, getUserConstraints } = BackMethods();
    const { userGovernorate, isSuperAdmin } = getUserConstraints();

    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [matrixData, setMatrixData] = useState(null);
    const [locationsData, setLocationsData] = useState([]);
    const [products, setProducts] = useState([]);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [activeLevel, setActiveLevel] = useState("governorate");
    const [drillDown, setDrillDown] = useState(null);
    const [chartModal, setChartModal] = useState(null);

    const [filters, setFilters] = useState({
        from: daysAgoIso(30),
        to: todayIso(),
        productId: "",
        governorate: isSuperAdmin ? "" : userGovernorate || "",
        city: "",
        district: "",
        establishmentUid: "",
    });

    const isSearchDisabled = !filters.from || !filters.to || !filters.productId;

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const res = await backEnd_request.get("/Locations?includeInactive=false", {
                    params: { size: 10000 },
                });
                setLocationsData(res.data?.data || []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchLocations();
    }, [backEnd_request]);

    const fetchProducts = useCallback(async () => {
        try {
            const res = await backEnd_request.get("/PriceSurveyProducts?includeInactive=true");
            setProducts(
                res.data.map((p) => ({
                    value: p.priceSurveyProductId,
                    label: p.nameAr || p.name,
                }))
            );
        } catch (err) {
            console.error(err);
        }
    }, [backEnd_request]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    useEffect(() => {
        if (searchParams.get("autoLoad") !== "1") return;
        const defaultGov = isSuperAdmin ? "" : userGovernorate || "";
        const restored = buildFiltersFromParams(searchParams, defaultGov);
        setFilters(restored);
        if (restored.from && restored.to && restored.productId) {
            fetchReport(restored);
        }
    }, [searchParams]);

    const availableGovernorates = useMemo(
        () => [...new Set(locationsData.map((l) => l.governorate).filter(Boolean))],
        [locationsData]
    );

    const availableCities = useMemo(() => {
        if (!filters.governorate) return [];
        return [
            ...new Set(
                locationsData
                    .filter((l) => l.governorate === filters.governorate)
                    .map((l) => l.city)
                    .filter(Boolean)
            ),
        ];
    }, [locationsData, filters.governorate]);

    const availableDistricts = useMemo(() => {
        if (!filters.governorate || !filters.city) return [];
        return [
            ...new Set(
                locationsData
                    .filter(
                        (l) =>
                            l.governorate === filters.governorate && l.city === filters.city
                    )
                    .map((l) => l.sector)
                    .filter(Boolean)
            ),
        ];
    }, [locationsData, filters.governorate, filters.city]);

    const selectedProduct = useMemo(
        () => products.find((p) => String(p.value) === String(filters.productId)) || null,
        [products, filters.productId]
    );

    const buildQueryParams = useCallback((filterValues, extra = {}) => {
        const level = resolveLevel(filterValues);
        const params = {
            productId: filterValues.productId,
            from: filterValues.from,
            to: filterValues.to,
            level,
            ...extra,
        };
        if (filterValues.governorate) params.governorate = filterValues.governorate;
        if (filterValues.city) params.city = filterValues.city;
        if (filterValues.district) params.district = filterValues.district;
        if (filterValues.establishmentUid) params.establishmentUid = filterValues.establishmentUid;
        return new URLSearchParams(params).toString();
    }, []);

    const fetchReport = async (overrideFilters = null) => {
        const activeFilters = overrideFilters ? { ...filters, ...overrideFilters } : filters;
        if (!activeFilters.from || !activeFilters.to || !activeFilters.productId) return;
        setLoading(true);
        try {
            const level = resolveLevel(activeFilters);
            const res = await backEnd_request.get(
                `/PriceSurveys/matrix/product-days?${buildQueryParams(activeFilters)}`
            );
            setMatrixData(res.data || null);
            setActiveLevel(level);
            if (overrideFilters) setFilters(activeFilters);
            setFiltersOpen(false);
        } catch (err) {
            console.error(err);
            setMatrixData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        if (!matrixData?.rows?.length) return;
        setExporting(true);
        try {
            const response = await backEnd_request.get(
                `/PriceSurveys/matrix/product-days?${buildQueryParams(filters, { exportXlsx: true })}`,
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `price-survey-product-days-${filters.productId}-${filters.from}-${filters.to}.xlsx`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Export error:", err);
        } finally {
            setExporting(false);
        }
    };

    const loadEstablishments = async (inputValue) => {
        if (!inputValue) return [];
        try {
            const res = await backEnd_request.get(
                `/Establishments?q=${inputValue}&page=1&size=20`
            );
            return (res.data?.data || []).map((est) => ({
                value: est.fieldUID,
                label: `${est.name} (${est.district || est.city || est.governorate})`,
            }));
        } catch {
            return [];
        }
    };

    const locationColumns = useMemo(() => {
        if (!matrixData?.columns) return [];
        return matrixData.columns.filter((col) => !isDateColumn(col));
    }, [matrixData]);

    const parsedRows = useMemo(() => {
        if (!matrixData?.rows || !matrixData?.columns) return [];

        const colIndex = {};
        matrixData.columns.forEach((col, i) => {
            colIndex[col] = i;
        });

        const dayIdx = matrixData.columns.findIndex((col) => isDateColumn(col));
        if (dayIdx < 0) return [];

        return matrixData.rows.map((row) => {
            const rawDate = row[dayIdx];
            const dayIso = String(rawDate ?? "").slice(0, 10);
            const prices = locationColumns.reduce((acc, loc) => {
                acc[loc] = row[colIndex[loc]];
                return acc;
            }, {});
            const hasSurvey = Object.values(prices).some(
                (v) => v != null && v !== "" && !Number.isNaN(Number(v))
            );
            return {
                date: rawDate,
                dayIso,
                dateLabel: formatDateLabel(rawDate),
                prices,
                hasSurvey,
            };
        });
    }, [matrixData, locationColumns]);

    const useScrollLayout = useMemo(() => {
        if (activeLevel === "district") return true;
        if (locationColumns.length > 7) return true;
        return locationColumns.some((col) => col.length > 10);
    }, [activeLevel, locationColumns]);

    const filterSummary = useMemo(() => {
        const parts = [
            selectedProduct?.label || `مادة #${filters.productId}`,
            `${new Date(filters.from).toLocaleDateString("ar-SY")} — ${new Date(filters.to).toLocaleDateString("ar-SY")}`,
            levelLabels[activeLevel],
        ];
        if (filters.governorate) parts.push(filters.governorate);
        if (filters.city) parts.push(filters.city);
        if (filters.district) parts.push(filters.district);
        return parts.join(" · ");
    }, [filters, activeLevel, selectedProduct]);

    const dateRangeLabel = matrixData
        ? `${new Date(matrixData.from).toLocaleDateString("ar-SY")} — ${new Date(matrixData.to).toLocaleDateString("ar-SY")}`
        : "";

    const canDrillDown = activeLevel === "governorate" || activeLevel === "city";

    const getDrillDownOptions = useCallback(
        (columnName, level) => {
            if (level === "governorate") {
                return [
                    ...new Set(
                        locationsData
                            .filter((l) => l.governorate === columnName)
                            .map((l) => l.city)
                            .filter(Boolean)
                    ),
                ];
            }
            if (level === "city") {
                return [
                    ...new Set(
                        locationsData
                            .filter(
                                (l) =>
                                    l.governorate === filters.governorate &&
                                    l.city === columnName
                            )
                            .map((l) => l.sector)
                            .filter(Boolean)
                    ),
                ];
            }
            return [];
        },
        [locationsData, filters.governorate]
    );

    const handleColumnClick = (columnName) => {
        if (!canDrillDown) return;
        const options = getDrillDownOptions(columnName, activeLevel);
        setDrillDown({
            columnName,
            nextLevel: activeLevel === "governorate" ? "city" : "district",
            options,
        });
    };

    const confirmDrillDown = () => {
        if (!drillDown) return;
        const updates =
            drillDown.nextLevel === "city"
                ? { governorate: drillDown.columnName, city: "", district: "" }
                : { city: drillDown.columnName, district: "" };
        setDrillDown(null);
        fetchReport(updates);
    };

    const handleDetailClick = (dateRow, locationName) => {
        if (activeLevel !== "district") return;
        const params = new URLSearchParams();
        params.set("from", dateRow.dayIso);
        params.set("to", addOneDay(dateRow.dayIso));
        params.set("productId", String(matrixData?.productId || filters.productId));
        if (filters.governorate) params.set("governorate", filters.governorate);
        if (filters.city) params.set("city", filters.city);
        params.set("district", locationName);
        params.set("returnTo", buildProductDaysReturnPath(filters));
        navigate(`/admin/price-survey-report?${params.toString()}`);
    };

    const handlePriceCellClick = (dateRow, loc) => {
        if (activeLevel === "district") {
            handleDetailClick(dateRow, loc);
            return;
        }
        if (canDrillDown) handleColumnClick(loc);
    };

    const handleDateChartClick = (dateRow) => {
        const data = locationColumns
            .map((loc) => ({
                location: loc,
                price: dateRow.prices[loc] != null ? Number(dateRow.prices[loc]) : null,
            }))
            .filter((item) => item.price != null && !Number.isNaN(item.price));

        setChartModal({
            dateLabel: dateRow.dateLabel,
            data,
        });
    };

    const chartUseHorizontal =
        (chartModal?.data?.length ?? 0) > 6 || activeLevel === "district";

    const chartYAxisWidth = useMemo(() => {
        if (!chartModal?.data?.length) return 140;
        return Math.min(
            Math.max(...chartModal.data.map((d) => d.location.length * 10), 140),
            300
        );
    }, [chartModal?.data]);

    const drillDownTitle =
        drillDown?.nextLevel === "city"
            ? `عرض مدن محافظة ${drillDown.columnName}`
            : `عرض قطاعات مدينة ${drillDown?.columnName}`;

    const drillDownHint =
        drillDown?.nextLevel === "city"
            ? "سيتم عرض أسعار المادة على مستوى المدن داخل هذه المحافظة"
            : "سيتم عرض أسعار المادة على مستوى القطاعات داخل هذه المدينة";

    const upLevelAction = useMemo(() => {
        if (!matrixData) return null;

        if (activeLevel === "district" && filters.city) {
            return {
                label: "رجوع إلى المدن",
                hint: `محافظة ${filters.governorate}`,
                updates: { city: "", district: "" },
            };
        }

        if (activeLevel === "city" && filters.governorate) {
            if (!isSuperAdmin && userGovernorate) return null;
            return {
                label: "رجوع إلى المحافظات",
                hint: filters.governorate,
                updates: { governorate: "", city: "", district: "" },
            };
        }

        return null;
    }, [
        matrixData,
        activeLevel,
        filters.governorate,
        filters.city,
        isSuperAdmin,
        userGovernorate,
    ]);

    const handleGoUpLevel = () => {
        if (!upLevelAction || loading) return;
        fetchReport(upLevelAction.updates);
    };

    return (
        <div
            className={`container-fluid px-3 px-md-4 py-4 ps-matrix-page ${!filtersOpen && matrixData ? "ps-matrix-filters-collapsed" : ""}`}
            dir="rtl"
        >
            <div className="ps-matrix-header mb-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                <div className="d-flex align-items-start gap-3 flex-wrap">
                    <button
                        type="button"
                        className="btn btn-outline-light btn-sm fw-bold d-flex align-items-center shadow-sm flex-shrink-0"
                        onClick={() => navigate("/admin")}
                    >
                        <FaHome className="ms-2" />
                        عودة للرئيسية
                    </button>
                    <div>
                    <h3>
                        <FaTable className="ms-2" />
                        تقرير سبر المادة — مصفوفة زمنية
                    </h3>
                    <p className="mb-0 mt-1 opacity-75 text-white">
                        {matrixData
                            ? `أسعار «${selectedProduct?.label || matrixData.productId}» يومياً على مستوى ${levelLabels[activeLevel]} — اضغط على التاريخ للرسم البياني${activeLevel === "district" ? " أو على خلية للتفاصيل" : ""}`
                            : "اختر المادة والمجال الزمني ثم اضغط عرض التقرير"}
                    </p>
                    </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    {matrixData && (
                        <>
                            <span className="meta-badge">
                                <FaCalendarAlt className="ms-1" />
                                {dateRangeLabel}
                            </span>
                            <span className="meta-badge">
                                <FaChartBar className="ms-1" />
                                {parsedRows.length} يوم
                            </span>
                        </>
                    )}
                    <button
                        type="button"
                        className="btn btn-light btn-sm fw-bold shadow-sm d-flex align-items-center"
                        disabled={!matrixData?.rows?.length || exporting}
                        onClick={handleExport}
                    >
                        {exporting ? (
                            <span className="spinner-border spinner-border-sm ms-2" />
                        ) : (
                            <FaFileExcel className="ms-2 text-success" />
                        )}
                        تصدير Excel
                    </button>
                </div>
            </div>

            {!filtersOpen && (
                <div className="ps-matrix-filters-summary mb-3">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <FaSlidersH className="text-primary" />
                            <span className="fw-bold small text-secondary">الفلاتر:</span>
                            <span className="small">{filterSummary}</span>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm fw-bold d-flex align-items-center"
                            onClick={() => setFiltersOpen(true)}
                        >
                            <FaChevronDown className="ms-1" />
                            تعديل الفلاتر
                        </button>
                    </div>
                </div>
            )}

            {filtersOpen && (
                <div className="ps-matrix-filters p-4 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold text-secondary mb-0">
                            <FaFilter className="ms-2 text-primary" />
                            خيارات البحث
                        </h6>
                        {matrixData && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-decoration-none p-0"
                                onClick={() => setFiltersOpen(false)}
                            >
                                <FaChevronUp className="ms-1" />
                                إخفاء
                            </button>
                        )}
                    </div>

                    <div className="row g-3">
                        <div className="col-12 col-lg-4">
                            <label className="text-danger">
                                <FaBox className="ms-1" />
                                المادة *
                            </label>
                            <Select
                                options={products}
                                placeholder="اختر المادة..."
                                styles={customStyles}
                                value={selectedProduct}
                                onChange={(opt) =>
                                    setFilters({ ...filters, productId: opt?.value || "" })
                                }
                            />
                        </div>

                        <div className="col-6 col-md-3 col-lg-2">
                            <label className="text-danger">
                                <FaCalendarAlt className="ms-1" />
                                من تاريخ *
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                value={filters.from}
                                max={filters.to || undefined}
                                onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                            />
                        </div>

                        <div className="col-6 col-md-3 col-lg-2">
                            <label className="text-danger">
                                <FaCalendarAlt className="ms-1" />
                                إلى تاريخ *
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                value={filters.to}
                                min={filters.from || undefined}
                                onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                            />
                        </div>

                        <div className="col-6 col-md-3 col-lg-2">
                            <label>المحافظة</label>
                            <select
                                className="form-select"
                                value={filters.governorate}
                                disabled={!isSuperAdmin && !!userGovernorate}
                                onChange={(e) =>
                                    setFilters({
                                        ...filters,
                                        governorate: e.target.value,
                                        city: "",
                                        district: "",
                                    })
                                }
                            >
                                <option value="">كل المحافظات</option>
                                {availableGovernorates.map((g) => (
                                    <option key={g} value={g}>
                                        {g}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-6 col-md-3 col-lg-2">
                            <label>المدينة</label>
                            <select
                                className="form-select"
                                value={filters.city}
                                disabled={!filters.governorate}
                                onChange={(e) =>
                                    setFilters({ ...filters, city: e.target.value, district: "" })
                                }
                            >
                                <option value="">كل المدن</option>
                                {availableCities.map((c) => (
                                    <option key={c} value={c}>
                                        {c}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-6 col-md-3 col-lg-2">
                            <label>القطاع</label>
                            <select
                                className="form-select"
                                value={filters.district}
                                disabled={!filters.city}
                                onChange={(e) =>
                                    setFilters({ ...filters, district: e.target.value })
                                }
                            >
                                <option value="">كل القطاعات</option>
                                {availableDistricts.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                            <label>المنشأة (اختياري)</label>
                            <AsyncSelect
                                cacheOptions
                                loadOptions={loadEstablishments}
                                placeholder="بحث عن منشأة..."
                                styles={customStyles}
                                isClearable
                                onChange={(opt) =>
                                    setFilters({ ...filters, establishmentUid: opt?.value || "" })
                                }
                            />
                        </div>

                        <div className="col-12 col-md-6 col-lg-2 d-flex align-items-end">
                            <button
                                type="button"
                                className="btn btn-primary w-100 fw-bold shadow-sm"
                                style={{ height: "42px" }}
                                disabled={isSearchDisabled || loading}
                                onClick={() => fetchReport()}
                            >
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm" />
                                ) : (
                                    <>
                                        <FaSearch className="ms-2" />
                                        عرض التقرير
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="alert alert-info py-2 px-3 mt-3 mb-0 small">
                        المستوى يُحدَّد تلقائياً: بدون محافظة → المحافظات، مع محافظة → المدن، مع
                        مدينة → القطاعات
                    </div>
                </div>
            )}

            {matrixData?.rows?.length > 0 && parsedRows.length > 0 && (
                <div className="ps-matrix-table-wrap">
                    {useScrollLayout && (
                        <div className="ps-matrix-scroll-hint">
                            ← مرّر أفقياً لعرض جميع {levelLabels[activeLevel]} →
                        </div>
                    )}
                    <div className={`ps-matrix-scroll ${useScrollLayout ? "ps-matrix-scroll--wide" : ""}`}>
                        <table className={`ps-matrix-table ${useScrollLayout ? "ps-matrix-table--scroll" : ""}`}>
                            <colgroup>
                                <col className="col-product-width" />
                                {locationColumns.map((col) => (
                                    <col key={col} className="col-price-width" />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className="col-product">التاريخ</th>
                                    {locationColumns.map((col) => (
                                        <th
                                            key={col}
                                            className={canDrillDown ? "col-drilldown" : ""}
                                            onClick={() => handleColumnClick(col)}
                                            title={col}
                                            style={
                                                useScrollLayout
                                                    ? { minWidth: `${Math.min(Math.max(col.length * 8, 90), 220)}px` }
                                                    : undefined
                                            }
                                        >
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {parsedRows.map((row, idx) => (
                                    <tr
                                        key={`${row.dayIso}-${idx}`}
                                        className={!row.hasSurvey ? "ps-matrix-row--no-survey" : ""}
                                    >
                                        <td
                                            className="col-product col-product-chart"
                                            title={`${row.dateLabel} — اضغط لعرض الرسم البياني`}
                                            onClick={() => handleDateChartClick(row)}
                                        >
                                            {row.dateLabel}
                                        </td>
                                        {locationColumns.map((loc) => {
                                            const raw = row.prices[loc];
                                            const formatted = formatPrice(raw, matrixData.currency);
                                            return (
                                                <td
                                                    key={loc}
                                                    className={`price-cell ${formatted ? "has-value" : "no-value"} ${canDrillDown || activeLevel === "district" ? "col-drilldown" : ""} ${activeLevel === "district" && formatted ? "col-detail-link" : ""}`}
                                                    onClick={() => handlePriceCellClick(row, loc)}
                                                    title={
                                                        activeLevel === "district"
                                                            ? formatted
                                                                ? `عرض تفاصيل السبر في ${loc} بتاريخ ${row.dateLabel}`
                                                                : `عرض تفاصيل ${loc}`
                                                            : canDrillDown
                                                              ? `اضغط لعرض ${activeLevel === "governorate" ? "مدن" : "قطاعات"} ${loc}`
                                                              : formatted
                                                                ? `${row.dateLabel} — ${loc}: ${formatted}`
                                                                : "لا توجد بيانات"
                                                    }
                                                >
                                                    {formatted ?? "—"}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!loading && !matrixData && (
                <div className="ps-matrix-empty">
                    <div className="icon">
                        <FaTable />
                    </div>
                    <h5 className="fw-bold text-secondary">ابدأ باختيار المادة والمجال الزمني</h5>
                    <p className="text-muted small mb-0">
                        حدّد المادة وتاريخ البداية والنهاية ثم اضغط «عرض التقرير»
                    </p>
                </div>
            )}

            {!loading && matrixData && (!matrixData.rows || matrixData.rows.length === 0) && (
                <div className="ps-matrix-empty">
                    <div className="icon">
                        <FaSearch />
                    </div>
                    <h5 className="fw-bold text-secondary">لا توجد بيانات في هذا المجال</h5>
                    <p className="text-muted small mb-0">
                        جرّب مجالاً زمنياً آخر أو غيّر المادة أو الفلاتر الجغرافية
                    </p>
                </div>
            )}

            {chartModal && (
                <div
                    className="ps-matrix-drill-overlay"
                    onClick={() => setChartModal(null)}
                    role="presentation"
                >
                    <div
                        className="ps-matrix-chart-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="ps-matrix-drill-modal__header">
                            <FaChartLine className="text-primary" />
                            <div className="flex-grow-1">
                                <h6 className="mb-0 fw-bold">{chartModal.dateLabel}</h6>
                                {selectedProduct?.label && (
                                    <small className="text-muted">{selectedProduct.label}</small>
                                )}
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-light border-0"
                                onClick={() => setChartModal(null)}
                                aria-label="إغلاق"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="ps-matrix-chart-modal__body">
                            {chartModal.data.length > 0 ? (
                                <>
                                    <div
                                        className="ps-matrix-chart-wrap"
                                        style={{
                                            height: chartUseHorizontal
                                                ? Math.max(360, chartModal.data.length * 44)
                                                : 360,
                                        }}
                                    >
                                        <ResponsiveContainer width="100%" height="100%">
                                            {chartUseHorizontal ? (
                                                <BarChart
                                                    data={chartModal.data}
                                                    layout="vertical"
                                                    margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                    <XAxis type="number" tickFormatter={(v) => v.toLocaleString("ar-SY")} />
                                                    <YAxis
                                                        type="category"
                                                        dataKey="location"
                                                        width={chartYAxisWidth}
                                                        tick={{ fontSize: 12 }}
                                                    />
                                                    <Tooltip content={<ChartTooltip currency={matrixData?.currency} />} />
                                                    <Bar dataKey="price" fill={CHART_BAR_FILL} radius={[0, 4, 4, 0]}>
                                                        <LabelList content={createHorizontalBarLabel(matrixData?.currency)} />
                                                    </Bar>
                                                </BarChart>
                                            ) : (
                                                <BarChart data={chartModal.data} margin={{ top: 8, right: 8, left: 8, bottom: 48 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                    <XAxis
                                                        dataKey="location"
                                                        tick={{ fontSize: 11 }}
                                                        interval={0}
                                                        angle={-35}
                                                        textAnchor="end"
                                                        height={70}
                                                    />
                                                    <YAxis tickFormatter={(v) => v.toLocaleString("ar-SY")} />
                                                    <Tooltip content={<ChartTooltip currency={matrixData?.currency} />} />
                                                    <Bar dataKey="price" fill={CHART_BAR_FILL} radius={[4, 4, 0, 0]}>
                                                        <LabelList content={createVerticalBarLabel(matrixData?.currency)} />
                                                    </Bar>
                                                </BarChart>
                                            )}
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="ps-matrix-chart-table-wrap mt-3">
                                        <table className="table table-sm mb-0 ps-matrix-chart-table text-end">
                                            <thead>
                                                <tr>
                                                    <th>{levelLabels[activeLevel]}</th>
                                                    <th className="text-center">السعر</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {chartModal.data.map((item) => (
                                                    <tr key={item.location}>
                                                        <td className="fw-semibold">{item.location}</td>
                                                        <td className="text-center fw-bold text-success">
                                                            {formatChartPrice(item.price, matrixData?.currency)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : (
                                <div className="alert alert-warning py-3 mb-0 text-center small">
                                    لا توجد أسعار مسجّلة في هذا اليوم للمواقع المعروضة
                                </div>
                            )}
                        </div>
                        <div className="ps-matrix-drill-modal__footer">
                            <button
                                type="button"
                                className="btn btn-primary btn-sm fw-bold"
                                onClick={() => setChartModal(null)}
                            >
                                إغلاق
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {drillDown && (
                <div
                    className="ps-matrix-drill-overlay"
                    onClick={() => setDrillDown(null)}
                    role="presentation"
                >
                    <div
                        className="ps-matrix-drill-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="ps-matrix-drill-modal__header">
                            <FaMapMarkerAlt className="text-primary" />
                            <h6 className="mb-0 fw-bold">{drillDownTitle}</h6>
                            <button
                                type="button"
                                className="btn btn-sm btn-light border-0"
                                onClick={() => setDrillDown(null)}
                                aria-label="إغلاق"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="ps-matrix-drill-modal__body">
                            <p className="text-muted small mb-3">{drillDownHint}</p>
                            {drillDown.options.length > 0 ? (
                                <>
                                    <p className="fw-bold small mb-2">
                                        {drillDown.nextLevel === "city"
                                            ? "المدن المتاحة:"
                                            : "القطاعات المتاحة:"}
                                    </p>
                                    <div className="ps-matrix-drill-tags">
                                        {drillDown.options.map((item) => (
                                            <span key={item} className="ps-matrix-drill-tag">
                                                {item}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="alert alert-warning py-2 px-3 mb-0 small">
                                    لا توجد{" "}
                                    {drillDown.nextLevel === "city" ? "مدن" : "قطاعات"} مسجّلة لهذا
                                    الموقع، يمكنك المتابعة على أي حال.
                                </div>
                            )}
                        </div>
                        <div className="ps-matrix-drill-modal__footer">
                            <button
                                type="button"
                                className="btn btn-light btn-sm fw-bold"
                                onClick={() => setDrillDown(null)}
                            >
                                إلغاء
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm fw-bold"
                                disabled={loading}
                                onClick={confirmDrillDown}
                            >
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm" />
                                ) : (
                                    <>
                                        <FaCheck className="ms-1" />
                                        تأكيد وعرض
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {upLevelAction && !drillDown && !chartModal && (
                <button
                    type="button"
                    className="ps-matrix-level-back"
                    onClick={handleGoUpLevel}
                    disabled={loading}
                    title={upLevelAction.hint}
                >
                    {loading ? (
                        <span className="spinner-border spinner-border-sm" />
                    ) : (
                        <FaArrowUp />
                    )}
                    <span className="ps-matrix-level-back__text">
                        <span className="ps-matrix-level-back__label">{upLevelAction.label}</span>
                        <span className="ps-matrix-level-back__hint">{upLevelAction.hint}</span>
                    </span>
                </button>
            )}
        </div>
    );
}
